import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataManager } from "@syncfusion/ej2-data";
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ODataAdaptor } from "../../adaptors/odata-adaptor";
import { CheckListModel } from '../../models/check-lists/check-list-model';
import {
  LienTache,
  Tache,
  TacheEtatUtilisateurCount, TacheSaveModel,
  TachesUtilisateur,
  TypeLien,
} from '../../models/taches/gestion-taches.model';
import { ToastSuccesModel } from '../../models/toasts/toast-succes.model';
import { ConfigService } from '../config/config.service';
import { NotificationToastService } from '../notifications/notification-toast.service';
import { LocalStorageTokenService } from '../utilisateurs/local-storage-token.service';

@Injectable({
  providedIn: 'root',
})
export class GestionProjetService {
  private readonly baseUrlApi = this.configService.config.urlApi;

  constructor(
    private client: HttpClient,
    private notificationToastService: NotificationToastService,
    private oDataAdaptor: ODataAdaptor,
    private configService: ConfigService,
    private localStorageTokenService: LocalStorageTokenService,
  ) {}

  getCheckListInscription(idInscription: number): Observable<CheckListModel> {
    return this.client.get<CheckListModel>(`${this.baseUrlApi}/api/g/ChecklistView/GetForInscription/${idInscription}`);
  }

  updateChecklistInscription(checklist: CheckListModel): Observable<any> {
    return this.client
      .post(`${this.baseUrlApi}/api/g/Checklist/definirChecklistInscription`, checklist)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('La check-list a été mise à jour.'))));
  }

  updateChecklistSession(checklist: CheckListModel): Observable<any> {
    return this.client
      .post(`${this.baseUrlApi}/api/g/Checklist/definirChecklistSession`, checklist)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('La check-list a été mise à jour.'))));
  }

  getCheckListSession(idSession: number): Observable<CheckListModel> {
    return this.client.get<CheckListModel>(`${this.baseUrlApi}/api/g/ChecklistView/GetForSession/${idSession}`);
  }

  getTache(idTache: number): Observable<Tache> {
    return this.client.get<Tache>(`${this.baseUrlApi}/api/g/taches/getTache?idTache=${idTache}`);
  }

  getTachesList(): Observable<TachesUtilisateur> {
    return this.client.get<TachesUtilisateur>(`${this.baseUrlApi}/api/g/taches/getTachesByUtilisateur`);
  }

  getTachesListByEtats(etats:number[]): Observable<TachesUtilisateur> {
    return this.client.post<TachesUtilisateur>(`${this.baseUrlApi}/api/g/taches/getTachesByUtilisateurAndEtats`, etats);
  }

  getTachesJusquaDateIncluseByUtilisateurAndEtats(dateIncluse:Date, etats:number[]): Observable<TachesUtilisateur> {
    return this.client.post<TachesUtilisateur>(`${this.baseUrlApi}/api/g/taches/getTachesJusquaDateIncluseByUtilisateurAndEtats`, {
      untilIncludedDate: dateIncluse,
      etats: etats
    });
  }

  getTachesSansDateListByEtats(etats:number[]): Observable<TachesUtilisateur> {
    return this.client.post<TachesUtilisateur>(`${this.baseUrlApi}/api/g/taches/getTachesSansDateByUtilisateurAndEtats`, etats);
  }

  getTacheEtatCounts(): Observable<TacheEtatUtilisateurCount[]> {
    return this.client.get<TacheEtatUtilisateurCount[]>(`${this.baseUrlApi}/api/g/taches/getTacheEtatCountsByUtilisateur`);
  }

  getTachesSession(idSession: number): Observable<Tache[]> {
    const typeLien = TypeLien.session
    return this.client.get<Tache[]>(`${this.baseUrlApi}/api/g/taches/GetTachesByLien?idLienDemande=${idSession}&typeLienDemande=${typeLien}`);
  }

  getLienPossible(idOriginal: number, type: number): Observable<LienTache> {
    return this.client.get<LienTache>(`${this.baseUrlApi}/api/g/taches/getLienPossible?idOriginal=${idOriginal}&type=${type}`);
  }

  buildDataManagerGetAgendaTachesPersonneAvecDate(): DataManager {
    return new DataManager({
      url: `${this.baseUrlApi}/api/g/PlanningTachesViewAvecDate/`,
      adaptor: this.oDataAdaptor,
    });
  }

  buildDataManagerGetTachesPersonne(idPersonne: number): DataManager {
    const typeLien = TypeLien.personne;
    return new DataManager({
      url: `${this.baseUrlApi}/api/g/TachesByLienView/?idLienDemande=${idPersonne}&typeLienDemande=${typeLien}`,
      adaptor: this.oDataAdaptor,
    });
  }

  buildDataManagerGetTachesEntreprise(idEntreprise: number): DataManager {
    const typeLien = TypeLien.entreprise;
    return new DataManager({
      url: `${this.baseUrlApi}/api/g/TachesByLienView/?idLienDemande=${idEntreprise}&typeLienDemande=${typeLien}`,
      adaptor: this.oDataAdaptor,
    });
  }

  buildDataManagerGetTachesFormateur(idFormateur: number): DataManager {
    const typeLien = TypeLien.formateur;
    return new DataManager({
      url: `${this.baseUrlApi}/api/g/TachesByLienView/?idLienDemande=${idFormateur}&typeLienDemande=${typeLien}`,
      adaptor: this.oDataAdaptor,
    });
  }

  getUrlLiensPossibles(forType: TypeLien) {
    return `${this.baseUrlApi}/api/g/LiensPossiblesView?type=${forType}`
  }

  getLiensActif(idTache: number): Observable<LienTache[]> {
    return this.client.get<LienTache[]>(`${this.baseUrlApi}/api/g/liensPossibles/getLiensActifByTacheId?idTache=${idTache}`);
  }

  deleteTache(idTache: number): Observable<any> {
    return this.client
      .delete(`${this.baseUrlApi}/api/g/taches/deleteTacheByUtilisateur?idTache=${idTache}`)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('La tâche a été supprimée.'))));
  }

  upsertTache(tache: Tache): Observable<any> {
    return this.client
      .post(`${this.baseUrlApi}/api/g/taches/upsertTacheByUtilisateur`, this.getTacheSave(tache))
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('La tâche a été mise à jour.'))));
  }

  private getTacheSave(from:Tache): TacheSaveModel {
    const liens:number[] = from.liens?.map(l => l.idLien) ?? [];
    return Object.assign(new TacheSaveModel(), from, {liens: liens});
  }
}
